import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { LogOut, Download, RefreshCw, Trash2, Mail, Building2, Calendar, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import Seo from "../components/Seo";

const STATUS_OPTS = ["new", "contacted", "qualified", "closed"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [stats, setStats] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [scores, setScores] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (window.location.hash && window.location.hash.includes("session_id=")) return;
    if (user) { load(); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await load();
      } catch {
        navigate("/admin/login", { replace: true });
      } finally { setChecking(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    try {
      const [s, a, c, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/growth-assessments"),
        api.get("/admin/contacts"),
        api.get("/admin/mogul-scores"),
      ]);
      setStats(s.data); setAssessments(a.data); setContacts(c.data); setScores(m.data);
    } catch (e) {
      toast.error("Failed to load data");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (kind, id, status) => {
    const path = kind === "ga" ? "growth-assessments" : kind === "ms" ? "mogul-scores" : "contacts";
    await api.patch(`/admin/${path}/${id}`, { status });
    toast.success("Status updated");
    load();
  };
  const del = async (kind, id) => {
    if (!window.confirm("Delete this submission?")) return;
    const path = kind === "ga" ? "growth-assessments" : kind === "ms" ? "mogul-scores" : "contacts";
    await api.delete(`/admin/${path}/${id}`);
    toast.success("Deleted");
    setSelected(null); load();
  };
  const exportCsv = (which = "mogul-scores") => {
    window.open(`${API}/admin/export/${which}.csv`, "_blank");
  };

  if (checking) return <div className="min-h-[60vh] flex items-center justify-center text-sm text-[#6B7280]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F7F7F8]" data-testid="page-admin">
      <Seo title="Admin Dashboard" path="/admin" noindex />
      <header className="bg-white border-b border-gray-100">
        <div className="container-wide h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-lg"><span>T</span><span className="text-gold">M</span><span>G</span> <span className="text-xs tracking-widest text-[#6B7280] uppercase ml-2">Admin</span></Link>
          <div className="flex items-center gap-4">
            {user && <div className="hidden sm:flex items-center gap-3 text-sm">
              {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
              <span className="text-[#4B5563]">{user.name || user.email}</span>
            </div>}
            <button onClick={logout} data-testid="admin-logout" className="btn btn-secondary text-sm py-2 px-3"><LogOut className="w-4 h-4" strokeWidth={1.5} /> Log out</button>
          </div>
        </div>
      </header>

      <div className="container-wide py-10">
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Mogul Scores" value={stats?.mogul_scores_total ?? 0} sub={`${stats?.mogul_scores_new ?? 0} new`} testid="stat-ms-total" highlight />
          <StatCard label="Growth Assessments" value={stats?.growth_assessments_total ?? 0} sub={`${stats?.growth_assessments_new ?? 0} new`} testid="stat-ga-total" />
          <StatCard label="Contact Messages" value={stats?.contacts_total ?? 0} sub={`${stats?.contacts_new ?? 0} new`} testid="stat-c-total" />
          <StatCard label="Total Leads" value={(stats?.mogul_scores_total ?? 0) + (stats?.growth_assessments_total ?? 0) + (stats?.contacts_total ?? 0)} sub="All-time" testid="stat-all-total" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-3xl">Submissions</h1>
          <div className="flex gap-3">
            <button onClick={load} className="btn btn-secondary text-sm py-2 px-3" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh</button>
            <button onClick={() => exportCsv("mogul-scores")} className="btn btn-primary text-sm py-2 px-3" data-testid="admin-export-csv"><Download className="w-4 h-4" strokeWidth={1.5} /> Export Scores CSV</button>
          </div>
        </div>

        <Tabs defaultValue="mogul" className="w-full" data-testid="admin-tabs">
          <TabsList className="bg-white border border-gray-100">
            <TabsTrigger value="mogul" data-testid="tab-mogul">Mogul Scores ({scores.length})</TabsTrigger>
            <TabsTrigger value="assessments" data-testid="tab-assessments">Growth Assessments ({assessments.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Messages ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="mogul">
            <ScoreTable rows={scores} onSelect={setSelected} onStatus={updateStatus} onDelete={del} />
          </TabsContent>
          <TabsContent value="assessments">
            <SubTable rows={assessments} kind="ga" onSelect={setSelected} onStatus={updateStatus} onDelete={del} />
          </TabsContent>
          <TabsContent value="contacts">
            <SubTable rows={contacts} kind="c" onSelect={setSelected} onStatus={updateStatus} onDelete={del} contactMode />
          </TabsContent>
        </Tabs>
      </div>

      {selected && <Drawer sub={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ScoreTable({ rows, onSelect, onStatus, onDelete }) {
  if (!rows.length) return <div className="mt-6 bg-white p-10 border border-gray-100 text-center text-[#6B7280]">No Mogul Score submissions yet.</div>;
  return (
    <div className="mt-6 bg-white border border-gray-100 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-100">
          <tr className="text-left text-xs uppercase tracking-widest text-[#6B7280]">
            <th className="py-3 px-4">Received</th>
            <th className="py-3 px-4">Name</th>
            <th className="py-3 px-4">Business</th>
            <th className="py-3 px-4">Industry</th>
            <th className="py-3 px-4">Score</th>
            <th className="py-3 px-4">Tier</th>
            <th className="py-3 px-4">Status</th>
            <th className="py-3 px-4"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-gray-100 hover:bg-[#FDFBF7]" data-testid={`score-row-${r.id}`}>
              <td className="py-3 px-4 text-[#4B5563] text-xs">{new Date(r.created_at).toLocaleString()}</td>
              <td className="py-3 px-4 font-medium">{r.full_name}</td>
              <td className="py-3 px-4 text-[#4B5563]">{r.business_name}</td>
              <td className="py-3 px-4 text-[#4B5563] capitalize">{r.industry}</td>
              <td className="py-3 px-4"><span className="font-serif text-lg">{r.total_score}</span></td>
              <td className="py-3 px-4 text-xs uppercase tracking-widest text-gold">{r.tier}</td>
              <td className="py-3 px-4">
                <select value={r.status} onChange={(e) => onStatus("ms", r.id, e.target.value)} className="text-xs bg-transparent border border-gray-200 px-2 py-1">
                  {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
              <td className="py-3 px-4 text-right whitespace-nowrap">
                <button onClick={() => onSelect({ ...r, __kind: "ms" })} className="text-xs text-gold hover:underline mr-3">View <ChevronRight className="inline w-3 h-3" /></button>
                <button onClick={() => onDelete("ms", r.id)} className="text-xs text-red-600 hover:underline"><Trash2 className="inline w-3 h-3" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatCard({ label, value, sub, testid, highlight }) {
  return (
    <div className={`p-6 border ${highlight ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-gray-100"}`} data-testid={testid}>
      <div className={`text-xs uppercase tracking-widest ${highlight ? "text-[#A1A1AA]" : "text-[#6B7280]"}`}>{label}</div>
      <div className="mt-2 font-serif text-3xl">{value}</div>
      <div className={`mt-1 text-xs ${highlight ? "text-gold" : "text-[#6B7280]"}`}>{sub}</div>
    </div>
  );
}

function SubTable({ rows, kind, onSelect, onStatus, onDelete, contactMode }) {
  if (!rows.length) return <div className="mt-6 bg-white p-10 border border-gray-100 text-center text-[#6B7280]">No submissions yet.</div>;
  return (
    <div className="mt-6 bg-white border border-gray-100 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-100">
          <tr className="text-left text-xs uppercase tracking-widest text-[#6B7280]">
            <th className="py-3 px-4">Received</th>
            <th className="py-3 px-4">Name</th>
            <th className="py-3 px-4">{contactMode ? "Subject" : "Business"}</th>
            <th className="py-3 px-4">Email</th>
            <th className="py-3 px-4">Status</th>
            <th className="py-3 px-4"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-gray-100 hover:bg-[#FDFBF7]" data-testid={`row-${r.id}`}>
              <td className="py-3 px-4 text-[#4B5563] text-xs">{new Date(r.created_at).toLocaleString()}</td>
              <td className="py-3 px-4 font-medium">{r.full_name}</td>
              <td className="py-3 px-4 text-[#4B5563]">{contactMode ? (r.subject || "—") : r.business_name}</td>
              <td className="py-3 px-4"><a href={`mailto:${r.email}`} className="hover:text-gold">{r.email}</a></td>
              <td className="py-3 px-4">
                <select value={r.status} onChange={(e) => onStatus(kind, r.id, e.target.value)} className="text-xs bg-transparent border border-gray-200 px-2 py-1">
                  {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
              <td className="py-3 px-4 text-right">
                <button onClick={() => onSelect({ ...r, __kind: kind })} className="text-xs text-gold hover:underline mr-3">View <ChevronRight className="inline w-3 h-3" /></button>
                <button onClick={() => onDelete(kind, r.id)} className="text-xs text-red-600 hover:underline"><Trash2 className="inline w-3 h-3" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Drawer({ sub, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-xl h-full overflow-y-auto p-8 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline mb-2">{sub.__kind === "ga" ? "Growth Assessment" : "Contact"}</div>
            <h2 className="font-serif text-2xl">{sub.full_name}</h2>
            <div className="mt-1 text-sm text-[#4B5563] flex items-center gap-4">
              <span className="flex gap-1 items-center"><Building2 className="w-3.5 h-3.5" strokeWidth={1.5} />{sub.business_name || "—"}</span>
              <span className="flex gap-1 items-center"><Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />{new Date(sub.created_at).toLocaleString()}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-sm text-[#6B7280]">Close</button>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
          {Object.entries(sub).filter(([k]) => !["id", "__kind", "created_at", "status"].includes(k)).map(([k, v]) => (
            <div key={k} className={["main_challenge", "desired_results", "products_services", "message"].includes(k) ? "col-span-2" : ""}>
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">{k.replace(/_/g, " ")}</div>
              <div className="mt-1 text-[#0A0A0A] break-words whitespace-pre-wrap">{Array.isArray(v) ? (v.join(", ") || "—") : (String(v) || "—")}</div>
            </div>
          ))}
        </div>
        <a href={`mailto:${sub.email}`} className="btn btn-primary mt-8"><Mail className="w-4 h-4" strokeWidth={1.5} /> Reply by email</a>
      </div>
    </div>
  );
}
